import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, EmptyValue, Form, LoadErrorState, Table } from '@/components/ui';
import { RateMode, Vehicle } from '../../../types/Vehicle';
import { formatMoney, rateDisplay } from '../utils/equipmentDisplay';
import { RATE_CLASSES_ENABLED } from '../utils/equipmentPermissions';
import vehicleService, { fieldErrorsOf, type RateHistoryRow } from '../../../services/vehicleService';
import { formatDateTime, todayPacific } from '../../../utils/wrapDate';
import VehicleBlueBookRatesSection from '../../vehicles/components/VehicleBlueBookRatesSection';

interface EquipmentRatesTabProps {
  vehicle: Vehicle;
  canEditRate: boolean;
  onUpdate: (vehicle: Vehicle) => void;
  /** Tells the page an unsaved rate is on screen, so leaving asks first (EQ-20). */
  onDirtyChange: (dirty: boolean) => void;
}

const FieldError: React.FC<{ message?: string }> = ({ message }) =>
  message ? <p className="tw-mb-0 tw-mt-1 tw-text-xs tw-text-danger" role="alert">{message}</p> : null;

const Required = () => <span className="tw-text-danger" aria-hidden="true"> *</span>;

/**
 * Rates (EQ-20, V3-061): the current rate, read-only or the section's own Edit with Save and
 * Cancel (V2-074). A change asks for an Effective Date (default today) and a Reason, and every
 * change is listed in Rate History. No approval step for now.
 */
const EquipmentRatesTab: React.FC<EquipmentRatesTabProps> = ({ vehicle, canEditRate, onUpdate, onDirtyChange }) => {
  const initial = vehicle.rate != null ? String(vehicle.rate) : '';
  const [editing, setEditing] = useState(false);
  const [rate, setRate] = useState(initial);
  const [effectiveDate, setEffectiveDate] = useState(todayPacific());
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [history, setHistory] = useState<RateHistoryRow[] | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const loadHistory = useCallback(() => {
    setHistoryError(null);
    vehicleService
      .getRateHistory(vehicle.id)
      .then(setHistory)
      .catch(() => setHistoryError('Could not load the rate history.'));
  }, [vehicle.id]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    setRate(initial);
  }, [initial]);

  const dirty = editing && (rate !== initial || reason.trim() !== '');
  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  const modeLabel =
    vehicle.rateMode === RateMode.Custom ? 'Custom' : vehicle.rateMode === RateMode.RateClass ? 'Rate Class' : 'Unit Rate';

  const startEdit = () => {
    setRate(initial);
    setEffectiveDate(todayPacific());
    setReason('');
    setErrors({});
    setEditing(true);
  };

  const cancel = () => {
    setRate(initial);
    setErrors({});
    setEditing(false);
  };

  const save = async () => {
    const next: Record<string, string> = {};
    if (rate.trim() === '' || !Number.isFinite(Number(rate)) || Number(rate) < 0) next.rate = 'Rate must be 0 or more';
    if (!effectiveDate) next.effectiveDate = 'Effective Date required';
    else if (effectiveDate > todayPacific()) next.effectiveDate = "Effective Date can't be in the future";
    if (!reason.trim()) next.reason = 'Reason required';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setSaving(true);
    try {
      onUpdate(await vehicleService.setRate(vehicle.id, { rate: Number(rate), effectiveDate, reason: reason.trim() }));
      onDirtyChange(false);
      setEditing(false);
      loadHistory();
    } catch (err) {
      setErrors(fieldErrorsOf(err, 'rate'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <Card>
        <Card.Header className="tw-flex tw-items-center tw-justify-between tw-gap-2">
          <h2 className="tw-mb-0 tw-text-base tw-font-semibold">Current Rate</h2>
          {canEditRate && !editing && (
            <Button size="sm" variant="outline-secondary" onClick={startEdit}>
              <i className="bi bi-pencil tw-mr-1" aria-hidden="true" />
              Edit
            </Button>
          )}
        </Card.Header>
        <Card.Body>
          {editing ? (
            <div className="tw-grid tw-grid-cols-1 tw-gap-3 sm:tw-grid-cols-3">
              <div>
                <Form.Label htmlFor="unit-rate">Rate ($ / h)<Required /></Form.Label>
                <Form.Control id="unit-rate" inputMode="decimal" className="tw-max-w-[10rem]" value={rate} onChange={(e) => setRate(e.target.value)} />
                <FieldError message={errors.rate} />
              </div>
              <div>
                <Form.Label htmlFor="unit-rate-effective">Effective Date<Required /></Form.Label>
                <Form.Control
                  id="unit-rate-effective"
                  type="date"
                  max={todayPacific()}
                  className="tw-max-w-[12rem]"
                  value={effectiveDate}
                  onChange={(e) => setEffectiveDate(e.target.value)}
                />
                <FieldError message={errors.effectiveDate} />
              </div>
              <div className="sm:tw-col-span-3">
                <Form.Label htmlFor="unit-rate-reason">Reason<Required /></Form.Label>
                <Form.Control id="unit-rate-reason" as="textarea" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
                <FieldError message={errors.reason || errors._form} />
              </div>
            </div>
          ) : (
            <dl className="tw-mb-0 tw-grid tw-grid-cols-1 tw-gap-3 sm:tw-grid-cols-3">
              <div>
                <dt className="tw-text-sm tw-font-semibold tw-text-content-secondary">Rate</dt>
                <dd className="tw-mb-0 tw-text-sm tw-text-content tw-tabular-nums">{rateDisplay(vehicle)}</dd>
              </div>
              <div>
                <dt className="tw-text-sm tw-font-semibold tw-text-content-secondary">Rate Type</dt>
                <dd className="tw-mb-0 tw-text-sm tw-text-content">{modeLabel}</dd>
              </div>
            </dl>
          )}
        </Card.Body>
        {editing && (
          <div className="tw-flex tw-justify-end tw-gap-2 tw-border-t tw-border-line tw-px-4 tw-py-3">
            <Button size="sm" variant="outline-secondary" onClick={cancel} disabled={saving}>
              Cancel
            </Button>
            <Button size="sm" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        )}
      </Card>

      <Card>
        <Card.Header>
          <h2 className="tw-mb-0 tw-text-base tw-font-semibold">Rate History</h2>
        </Card.Header>
        <Card.Body className="tw-p-0">
          {historyError ? (
            <div className="tw-p-4">
              <LoadErrorState what="the rate history" onRetry={loadHistory} />
            </div>
          ) : history === null ? (
            <p className="tw-mb-0 tw-p-4 tw-text-sm tw-text-content-muted">Loading the rate history…</p>
          ) : history.length === 0 ? (
            <p className="tw-mb-0 tw-p-4 tw-text-sm tw-text-content-muted">No rate changes recorded yet.</p>
          ) : (
            <Table responsive className="tw-mb-0">
              <thead>
                <tr>
                  <th className="tw-text-right">Rate</th>
                  <th>Effective</th>
                  <th className="tw-min-w-[14rem]">Reason</th>
                  <th>Changed By</th>
                  <th>Changed</th>
                </tr>
              </thead>
              <tbody>
                {history.map((row) => (
                  <tr key={row.id}>
                    <td className="tw-whitespace-nowrap tw-text-right tw-tabular-nums">{formatMoney(row.rate)} / h</td>
                    <td className="tw-whitespace-nowrap tw-tabular-nums">
                      {row.effectiveFrom} – {row.effectiveTo ?? 'Now'}
                    </td>
                    <td className="tw-whitespace-pre-wrap">{row.reason}</td>
                    <td>{row.changedBy || <EmptyValue />}</td>
                    <td className="tw-whitespace-nowrap">{formatDateTime(row.changedAt) ?? <EmptyValue />}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
      {RATE_CLASSES_ENABLED && <VehicleBlueBookRatesSection vehicle={vehicle} onUpdate={onUpdate} />}
    </div>
  );
};

export default EquipmentRatesTab;
